import { loadStoredProducts, syncStoredProducts } from './inventory-storage';
/**
 * src/domain/inventory/PersistentInventoryRepository.ts
 *
 * Transactional & Optimistic-Locked Inventory Repository.
 * Enforces Default-Deny Data Projection: queries without an explicit profile
 * default to the least-privileged `publicPortalConfig` to prevent data leaks.
 */

import { DomainProduct, IStockReservation, PosProduct } from './types/product-types';
import { ProfileConfig } from '../../config/profiles/types';
import { publicPortalConfig } from '../../config/profiles/public-portal.config';
import { ProductProjection } from './ProductProjection';

export interface InventoryRepository {
  getProduct(id: string, profile?: ProfileConfig): Promise<DomainProduct | null>;
  getBySku(sku: string, profile?: ProfileConfig): Promise<DomainProduct | null>;
  listProducts(filter?: { type?: string; status?: string }, profile?: ProfileConfig): Promise<DomainProduct[]>;
  save(product: DomainProduct): Promise<void>;
  getRawInternal(id: string): Promise<DomainProduct | null>;
  listRawInternal(filter?: { type?: string; status?: string }): Promise<DomainProduct[]>;
  reserveStock(
    sku: string,
    quantity: number,
    reservationId: string,
    holdTtlMs?: number
  ): Promise<{ success: boolean; reason?: 'INSUFFICIENT_STOCK' | 'IDEMPOTENT_HIT' | 'VERSION_CONFLICT' }>;
  releaseExpiredReservations(): Promise<number>;
  get(id: string): Promise<DomainProduct | null>; // Legacy alias pointing to default-deny getProduct
  list(filter?: { type?: string; status?: string }): Promise<DomainProduct[]>; // Legacy alias pointing to default-deny listProducts
}

export class PersistentInventoryRepository implements InventoryRepository {
  private products: Map<string, DomainProduct> = new Map();
  private reservations: Map<string, IStockReservation> = new Map();
  private expiryTimer: any = null;
  constructor() {
    for (const p of loadStoredProducts()) this.products.set(p.id, p);
    this.startExpiryDaemon();
  }

  public async getProduct(
    id: string,
    profile: ProfileConfig = publicPortalConfig
  ): Promise<DomainProduct | null> {
    const raw = this.products.get(id);
    if (!raw) return null;
    return ProductProjection.projectProductForProfile(raw, profile);
  }

  public async getBySku(
    sku: string,
    profile: ProfileConfig = publicPortalConfig
  ): Promise<DomainProduct | null> {
    for (const p of this.products.values()) {
      if (p.sku === sku) {
        return ProductProjection.projectProductForProfile(p, profile);
      }
    }
    return null;
  }

  public async listProducts(
    filter?: { type?: string; status?: string },
    profile: ProfileConfig = publicPortalConfig
  ): Promise<DomainProduct[]> {
    const all = Array.from(this.products.values()).filter((p) => {
      if (filter?.type && p.type !== filter.type) return false;
      if (filter?.status && p.status !== filter.status) return false;
      return true;
    });

    return all.map((p) => ProductProjection.projectProductForProfile(p, profile));
  }

  // Legacy aliases defaulting to public-portal least privilege
  public async get(id: string): Promise<DomainProduct | null> {
    return this.getProduct(id, publicPortalConfig);
  }

  public async list(filter?: { type?: string; status?: string }): Promise<DomainProduct[]> {
    return this.listProducts(filter, publicPortalConfig);
  }

  /** @internal Protected raw retrieval */
  public async getRawInternal(id: string): Promise<DomainProduct | null> {
    return this.products.get(id) || null;
  }

  /** @internal Protected raw listing */
  public async listRawInternal(filter?: { type?: string; status?: string }): Promise<DomainProduct[]> {
    return Array.from(this.products.values()).filter((p) => {
      if (filter?.type && p.type !== filter.type) return false;
      if (filter?.status && p.status !== filter.status) return false;
      return true;
    });
  }

  public async save(product: DomainProduct): Promise<void> {
    const existing = this.products.get(product.id);
    const updated: DomainProduct = {
      ...product,
      version: existing ? existing.version + 1 : 1,
      updatedAt: new Date().toISOString(),
    };
    this.products.set(product.id, updated);
    syncStoredProducts(this.products.values());
  }

  public async reserveStock(
    sku: string,
    quantity: number,
    reservationId: string,
    holdTtlMs: number = 15 * 60 * 1000
  ): Promise<{ success: boolean; reason?: 'INSUFFICIENT_STOCK' | 'IDEMPOTENT_HIT' | 'VERSION_CONFLICT' }> {
    const existingReservation = this.reservations.get(reservationId);
    if (existingReservation && existingReservation.expiresAt > Date.now()) {
      return { success: true, reason: 'IDEMPOTENT_HIT' };
    }

    let rawProduct: PosProduct | null = null;
    for (const p of this.products.values()) {
      if (p.sku === sku && p.type === 'pos') {
        rawProduct = p as PosProduct;
        break;
      }
    }

    if (!rawProduct) {
      return { success: false, reason: 'INSUFFICIENT_STOCK' };
    }

    let activeReserved = 0;
    for (const res of this.reservations.values()) {
      if (res.sku === sku && res.expiresAt > Date.now()) {
        activeReserved += res.quantity;
      }
    }

    if (rawProduct.metadata.stockLevel - activeReserved < quantity) {
      return { success: false, reason: 'INSUFFICIENT_STOCK' };
    }

    this.reservations.set(reservationId, {
      reservationId,
      sku,
      quantity,
      expiresAt: Date.now() + holdTtlMs,
    });

    return { success: true };
  }

  public async releaseExpiredReservations(): Promise<number> {
    const now = Date.now();
    let releasedCount = 0;
    for (const [id, res] of this.reservations.entries()) {
      if (res.expiresAt <= now) {
        this.reservations.delete(id);
        releasedCount++;
      }
    }
    return releasedCount;
  }

  public stopExpiryDaemon(): void {
    if (this.expiryTimer) clearInterval(this.expiryTimer);
  }

  private startExpiryDaemon(): void {
    this.expiryTimer = setInterval(() => {
      this.releaseExpiredReservations();
    }, 60000);
  }
}
